import type { BuiltInTool, BuiltInToolError } from './types'

const MAILBOX =
    "The run's assigned member. The mailbox is always theirs: a `memberId` in the payload is ignored, and an OS-owned task has no mailbox."

const NO_MAILBOX: readonly BuiltInToolError[] = [
    { code: 'no_assigned_member', when: 'The run has no assigned member, so no mailbox.', handling: 'tool_error' },
]

const THREAD_NOT_FOUND: BuiltInToolError = {
    code: 'thread_not_found',
    when: "The threadKey names no conversation in this member's mailbox, including another member's.",
    handling: 'tool_error',
}

/**
 * The weekday chase over threads the assigned member replied to that went unanswered (os#399). A run is offered
 * only the threads to act on today, and answers each with a draft or a snooze. The draft is a follow-up through
 * the gated `os.mail_reply` against the thread's `replyToEmailId`, so it waits for the CEO like every other send;
 * nothing here sends. Their own family, so the mailbox tools stay exactly the four `os.mail` names.
 */
export const OS_MAIL_CHASE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.mail_quiet_threads',
        family: 'os.mail_chase',
        summary: 'The threads the assigned member replied to that have gone quiet, oldest first, at most ten.',
        description:
            'List the conversations in the assigned member’s mailbox where the member had the last word and the correspondent has not answered for three clear weekdays, counted in the enterprise’s timezone. A thread already chased or snoozed today, one snoozed until a later date, and one whose original names no address a reply could go to are left out, so every row is one to act on today. At most the ten oldest.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            '`{ data }`: each row has `threadKey`, `replyToEmailId` (the newest inbound message: pass it to os.mail_reply as `id`), `correspondent`, `subject` and `lastOutboundAt`. An empty list when nothing is quiet or the member has no mailbox.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: NO_MAILBOX,
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.mail_mark_chased',
        family: 'os.mail_chase',
        summary: 'Record that a quiet thread was answered today, so no second run today raises it again.',
        description:
            'Record that one thread from os.mail_quiet_threads has been dealt with today. Call it before os.mail_reply on that thread: the reply waits for the CEO, and a second run the same day must not propose a second follow-up while the first is still waiting. The thread is offered again on a later weekday if it is still quiet.',
        inputSchema: {
            type: 'object',
            properties: { threadKey: { type: 'string', description: 'A threadKey from os.mail_quiet_threads' } },
            required: ['threadKey'],
            additionalProperties: false,
        },
        returns: '`{ threadKey, chasedAt }`.',
        callers: ['run'],
        effect: 'write',
        sideEffects: "Stores when the thread was last chased on the member's chase record for it. Nothing is sent.",
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: [...NO_MAILBOX, THREAD_NOT_FOUND],
        examples: [{ input: { threadKey: '<0f1e2d3c@mail.example.com>' } }],
        public: true,
    },
    {
        name: 'os.mail_snooze',
        family: 'os.mail_chase',
        summary: 'Leave a quiet thread alone until a date, instead of drafting a follow-up.',
        description:
            'Snooze one thread from os.mail_quiet_threads until a later date (YYYY-MM-DD or an ISO timestamp): it is not offered again until that date has passed or the member writes in the thread again, and it counts as dealt with today. Use it instead of a follow-up when chasing now would be premature. Nothing is sent.',
        inputSchema: {
            type: 'object',
            properties: {
                threadKey: { type: 'string', description: 'A threadKey from os.mail_quiet_threads' },
                until: { type: 'string', description: 'When to raise it again: a date or timestamp after now' },
            },
            required: ['threadKey', 'until'],
            additionalProperties: false,
        },
        returns: '`{ threadKey, snoozedUntil }`.',
        callers: ['run'],
        effect: 'write',
        sideEffects: "Stores the snooze on the member's chase record for the thread. Nothing is sent.",
        retry: 'safe',
        needs: { member: MAILBOX },
        errors: [
            ...NO_MAILBOX,
            THREAD_NOT_FOUND,
            {
                code: 'snooze_until_invalid',
                when: '`until` is not a date, or is not after now.',
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { threadKey: '<0f1e2d3c@mail.example.com>', until: '2026-10-12' } }],
        public: true,
    },
]
